import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReflectionType } from "../../../../../../types/domain-types";
import { DtoCubeDataResponse } from "../../../../../../types/dto-types";
import { PersonalityMatrixAdvanced } from "../../../../ParticipationFlow/components/personality-matrix-advanced";
import { CATEGORY_ICONS } from "../../../components/ChallengeRow";
import { DoubleToggle } from "../../../components/DoubleToggle";
import { Cube } from "../../../components/Сube";
import { CUBE_SIDE_NUMBER_TO_NAME } from "../../../components/Сube/constants";
import { CubeSideName } from "../../../components/Сube/types";
import { SelectedFilters, useUrlState } from "../../../hooks";
import { useReflectionHomePageData } from "../../../hooks/useReflectionHomePageData";
import { CategoryNavItem } from "../../../types";
import { calculatePercentagesForNumbers, renderNumber } from "../../../utils";
import { getReflectionTranslations } from "../translations";
import "./style.scss";

export function HomePage({
  rawData,
  currentTestId,
  selectedFilters,
  selectedReflectionType,
  setSelectedReflectionType,
}: {
  rawData: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters;
  selectedReflectionType: ReflectionType;
  setSelectedReflectionType: (type: ReflectionType) => void;
}) {
  const { t } = useTranslation();
  const { updatePath } = useUrlState();

  const translations = getReflectionTranslations(t);

  const [selectedPersonalityMatrixView, setSelectedPersonalityMatrixView] =
    useState<"precise" | "clustered">("precise");

  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();

  function navigateToCategory(category: CategoryNavItem) {
    updatePath({ category });
  }

  const { cubeValuesByCategory, personalityPoints } = useReflectionHomePageData(
    {
      rawData,
      currentTestId,
      selectedFilters,
    }
  );

  const sidesSaturation = calculatePercentagesForNumbers({
    "2": cubeValuesByCategory.organization?.[selectedReflectionType],
    "3": cubeValuesByCategory.leadership?.[selectedReflectionType],
    "4": cubeValuesByCategory.team?.[selectedReflectionType],
  });

  const renderCubeInfo = (category: CategoryNavItem) => {
    const categoryData = cubeValuesByCategory?.[category];
    const value =
      categoryData?.[selectedReflectionType as keyof typeof categoryData] ?? 0;
    const title = translations.categories[category]!.title;
    const isActive =
      hoveredCubeSide && CUBE_SIDE_NUMBER_TO_NAME[hoveredCubeSide] === category;

    return (
      <div
        className={`cube-info ${isActive ? "active" : ""}`}
        onClick={() => navigateToCategory(category)}
        style={{ cursor: "pointer" }}
      >
        <div className="cube-inner">
          <div className="cube-title">
            <div className="cube-icon">{(CATEGORY_ICONS as any)[category]}</div>
            <div>{title}</div>
          </div>
          <div className="cube-value">{renderNumber(value, false)}%</div>
        </div>
      </div>
    );
  };

  return (
    <div className="reflection-home-page">
      <div className="left">
        <section className="cube-overview">
          <section className="cube-section">
            <div className="cube-wrap">
              <div className="cube-top">{renderCubeInfo("organization")}</div>
              <div className="cube-center">
                <Cube
                  greyOutOtherSidesOnHover
                  values={{ 1: 0, 2: 100, 3: 100, 4: 100, 5: 0, 6: 0 }}
                  sidesSaturation={sidesSaturation}
                  mode="collapsed"
                  onSideHover={side => {
                    setHoveredCubeSide(side);
                  }}
                  onSideClick={side => {
                    updatePath({
                      category: CUBE_SIDE_NUMBER_TO_NAME[
                        side
                      ] as CategoryNavItem,
                    });
                  }}
                />
              </div>
              <div className="cube-bottom">
                {renderCubeInfo("team")}
                {renderCubeInfo("leadership")}
              </div>
            </div>
          </section>
          <section className="cube-toggle">
            <DoubleToggle
              leftOption={{
                id: ReflectionType.Leverage,
                label: t(
                  "frontend.futurebilityCockpit.reflection.leverage",
                  "Leverage"
                ),
              }}
              rightOption={{
                id: ReflectionType.Challenge,
                label: t(
                  "frontend.futurebilityCockpit.reflection.challenges",
                  "Challenges"
                ),
              }}
              selectedOptionId={selectedReflectionType}
              onChange={optionId => {
                setSelectedReflectionType(optionId as ReflectionType);
              }}
            />
          </section>
        </section>
      </div>
      <div className="right">
        <PersonalityMatrixAdvanced
          isClustered={selectedPersonalityMatrixView === "clustered"}
          points={personalityPoints}
          labels={{
            ...translations.personalityMatrix.labels,
          }}
          content={{
            ...translations.personalityMatrix.content,
          }}
        />
        <div className="right-toggle-wrap">
          <DoubleToggle
            leftOption={{
              id: "precise",
              label: t(
                "frontend.futurebilityCockpit.quadrantToggle.precise",
                "Precise"
              ),
            }}
            rightOption={{
              id: "clustered",
              label: t(
                "frontend.futurebilityCockpit.quadrantToggle.clustered",
                "Clustered"
              ),
            }}
            selectedOptionId={selectedPersonalityMatrixView}
            onChange={optionId => {
              setSelectedPersonalityMatrixView(
                optionId as "precise" | "clustered"
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
